/**
 * FERP PDF Redaction Tool Overlay (DRR-079)
 *
 * Marks rectangular regions for server-side redaction processing.
 * Redaction is NEVER applied client-side — only marks are created.
 * Server-side ferp-pdf crate handles actual content removal.
 */

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import { Box, Text } from '@mantine/core';
import { useAuth } from '@/auth';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfAnnotation, PdfRect } from './types';

interface RedactionToolOverlayProps {
  page: number;
  scale: number;
  annotations: PdfAnnotation[];
}

export function RedactionToolOverlay({ page, scale, annotations }: RedactionToolOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ x: number; y: number } | null>(null);
  const { state, addAnnotation } = usePdfSuite();
  const { user } = useAuth();

  const getRelative = useCallback((e: MouseEvent) => {
    const el = overlayRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (state.viewer.annotationMode !== 'redaction') return;
      const pt = getRelative(e);
      if (pt) {
        setDragStart(pt);
        setDragEnd(pt);
      }
    },
    [state.viewer.annotationMode, getRelative],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStart) return;
      const pt = getRelative(e);
      if (pt) setDragEnd(pt);
    },
    [dragStart, getRelative],
  );

  const handleMouseUp = useCallback(() => {
    if (!dragStart || !dragEnd) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const rect: PdfRect = {
      x: Math.min(dragStart.x, dragEnd.x),
      y: Math.min(dragStart.y, dragEnd.y),
      width: Math.abs(dragEnd.x - dragStart.x),
      height: Math.abs(dragEnd.y - dragStart.y),
    };

    if (rect.width > 0.01 && rect.height > 0.01) {
      addAnnotation({
        id: `pending_${Date.now()}`,
        type: 'redaction',
        page,
        rect,
        content: 'REDACTED',
        color: '#000000',
        redactionPending: true,
        createdBy: user?.id ?? 'unknown',
        createdAt: new Date().toISOString(),
      });
    }

    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, page, addAnnotation, user?.id]);

  const redactionAnnotations = annotations.filter((a) => a.type === 'redaction');

  return (
    <Box
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: state.viewer.annotationMode === 'redaction' ? 'auto' : 'none',
        cursor: state.viewer.annotationMode === 'redaction' ? 'crosshair' : 'default',
        zIndex: 14,
      }}
    >
      {/* Existing redaction marks */}
      {redactionAnnotations.map((ann) => (
        <Box
          key={ann.id}
          style={{
            position: 'absolute',
            left: `${ann.rect.x * 100}%`,
            top: `${ann.rect.y * 100}%`,
            width: `${ann.rect.width * 100}%`,
            height: `${ann.rect.height * 100}%`,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Text size="xs" c="white" fw={700} style={{ letterSpacing: 2 }}>
            REDACTED
          </Text>
        </Box>
      ))}

      {/* Drag preview */}
      {dragStart && dragEnd && (
        <Box
          style={{
            position: 'absolute',
            left: `${Math.min(dragStart.x, dragEnd.x) * 100}%`,
            top: `${Math.min(dragStart.y, dragEnd.y) * 100}%`,
            width: `${Math.abs(dragEnd.x - dragStart.x) * 100}%`,
            height: `${Math.abs(dragEnd.y - dragStart.y) * 100}%`,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            border: '2px dashed red',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
